import { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ReactECharts from 'echarts-for-react';
import { OutwriteApi } from '../services/OutwriteService';
import { outwriteTaskToEchartsOption } from '../outwrite/outwriteChartAdapter';
import OutwriteTaskReviewPanel from './OutwriteTaskReviewPanel';
import OutwriteLlmModelSelect from './OutwriteLlmModelSelect';
import OutwriteCollapsibleTable from './OutwriteCollapsibleTable';
import { OUTWRITE_TESTS_LOAD_KEY } from './OutwriteTests';

/** Cloud agent CHECK runs can take several minutes; align with PoC / nginx. */
const CHECK_TIMEOUT_MS = 15 * 60 * 1000;

function consumeTestsLoadPayload() {
  try {
    const raw = sessionStorage.getItem(OUTWRITE_TESTS_LOAD_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(OUTWRITE_TESTS_LOAD_KEY);
    const payload = JSON.parse(raw);
    if (!payload?.taskId) return null;
    return {
      taskId: String(payload.taskId),
      answerText:
        typeof payload.answerText === 'string' ? payload.answerText : '',
    };
  } catch {
    return null;
  }
}

const ROW_CREATED_BG = '#e8f5e9';
const ROW_DELETED_BG = '#ffebee';
const CELL_CHANGED_BG = '#fff8e1';

function mapLoadedRubrics(list) {
  return (Array.isArray(list) ? list : []).map((r) => ({
    id: r.id,
    rule: r.rule,
    severity: r.severity,
    message: r.message,
    shownInPhase01: !!r.shownInPhase01,
    occursInPractice:
      r.occursInPractice == null ? '' : String(r.occursInPractice),
    note: r.note ?? '',
    example: r.example,
    block: r.block ?? '',
    detectedBy: r.detectedBy ?? '',
    _originalId: r.id,
    _originalRule: r.rule,
    _originalSeverity: r.severity ?? '',
    _originalMessage: r.message ?? '',
    _originalShownInPhase01: !!r.shownInPhase01,
    _originalOccursInPractice:
      r.occursInPractice == null ? '' : String(r.occursInPractice),
    _originalNote: r.note ?? '',
    _originalBlock: r.block ?? '',
    _originalDetectedBy: r.detectedBy ?? '',
  }));
}

function formatKeyValue(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function formatExplanation(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function toRubricPayload(row) {
  return {
    id: row.id.trim(),
    rule: row.rule,
    ...(row.severity != null && row.severity !== ''
      ? { severity: row.severity }
      : {}),
    ...(row.message != null && row.message !== ''
      ? { message: row.message }
      : {}),
    ...(row.note != null ? { note: row.note } : {}),
    ...(row.block != null ? { block: row.block } : {}),
    ...(row.detectedBy != null ? { detectedBy: row.detectedBy } : {}),
    ...(row.shownInPhase01 != null
      ? { shownInPhase01: row.shownInPhase01 }
      : {}),
    ...(row.occursInPractice != null
      ? { occursInPractice: row.occursInPractice }
      : {}),
    ...(row.example != null ? { example: row.example } : {}),
  };
}

/**
 * Outwrite tasks page: task selector + chart + review + editable rubrics + CHECK table.
 */
export default function OutwriteTasks() {
  const initialTestsLoadRef = useRef(null);
  if (initialTestsLoadRef.current === null) {
    initialTestsLoadRef.current = consumeTestsLoadPayload();
  }
  const [tasks, setTasks] = useState([]);
  const [selectedId, setSelectedId] = useState(
    () => initialTestsLoadRef.current?.taskId || '',
  );
  const [task, setTask] = useState(null);
  const [rubrics, setRubrics] = useState([]);
  const [rubricsBaseline, setRubricsBaseline] = useState([]);
  const [editRubrics, setEditRubrics] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [taskLoading, setTaskLoading] = useState(false);
  const [listError, setListError] = useState(null);
  const [answerText, setAnswerText] = useState('');
  const [checkResult, setCheckResult] = useState(null);
  /** Per rubric_id: { expected: boolean, comment: string } */
  const [checkAnnotations, setCheckAnnotations] = useState({});
  const [checkLoading, setCheckLoading] = useState(false);
  const [annotationSaveLoading, setAnnotationSaveLoading] = useState(false);
  const [makeTestLoading, setMakeTestLoading] = useState(false);
  const [makeTestMessage, setMakeTestMessage] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [severityOptions, setSeverityOptions] = useState([
    'minor',
    'substantial',
    'critical',
  ]);
  const [toFinds, setToFinds] = useState([]);
  const [toFindsBaseline, setToFindsBaseline] = useState([]);
  const [editToFinds, setEditToFinds] = useState(false);
  const [toFindsSaveLoading, setToFindsSaveLoading] = useState(false);
  const [promptRules, setPromptRules] = useState('');
  const [promptBaseline, setPromptBaseline] = useState('');
  const [editPrompt, setEditPrompt] = useState(false);
  const [promptSaveLoading, setPromptSaveLoading] = useState(false);
  /** One-shot answer restore after TESTS → TASKS load (no Expected/Comment). */
  const pendingLoadAnswerRef = useRef(
    initialTestsLoadRef.current?.answerText ?? null,
  );

  useEffect(() => {
    let cancelled = false;
    OutwriteApi.listRubricsSeverities()
      .then((list) => {
        if (cancelled || !Array.isArray(list)) return;
        const values = list
          .map((row) => (typeof row === 'string' ? row : row?.value))
          .filter(Boolean);
        if (values.length) setSeverityOptions(values);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setListLoading(true);
    setListError(null);
    OutwriteApi.list()
      .then((items) => {
        if (cancelled) return;
        const list = items || [];
        setTasks(list);
        if (list.length && !selectedId) {
          setSelectedId(list[0].id);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setListError(err?.message || 'Failed to load tasks');
        }
      })
      .finally(() => {
        if (!cancelled) setListLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // intentionally once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setTask(null);
      setRubrics([]);
      setRubricsBaseline([]);
      setTaskLoading(false);
      return undefined;
    }
    let cancelled = false;
    setTaskLoading(true);
    setAnswerText('');
    setCheckResult(null);
    setEditRubrics(false);
    setEditToFinds(false);
    setEditPrompt(false);
    OutwriteApi.getWorkspace(selectedId)
      .then((data) => {
        if (cancelled) return;
        setTask(data);
        const mapped = mapLoadedRubrics(data?.rubrics);
        setRubrics(mapped);
        setRubricsBaseline(mapped);
        const finds = Array.isArray(data?.to_finds)
          ? data.to_finds.map((row) => ({ ...row }))
          : [];
        setToFinds(finds);
        setToFindsBaseline(finds.map((row) => ({ ...row })));
        if (pendingLoadAnswerRef.current != null) {
          setAnswerText(pendingLoadAnswerRef.current);
          pendingLoadAnswerRef.current = null;
        }
        if (data?.task_type) {
          return OutwriteApi.getCheckPrompt(data.task_type).then((prompt) => {
            if (cancelled) return;
            const text = prompt?.rulesText || '';
            setPromptRules(text);
            setPromptBaseline(text);
          });
        }
        setPromptRules('');
        setPromptBaseline('');
        return undefined;
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setTaskLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  const option = useMemo(
    () => (task ? outwriteTaskToEchartsOption(task) : null),
    [task],
  );

  const activeRubrics = useMemo(
    () => rubrics.filter((r) => !r.deleted),
    [rubrics],
  );

  const ruleById = useMemo(() => {
    const map = new Map();
    activeRubrics.forEach((r) => map.set(r.id, r.rule));
    return map;
  }, [activeRubrics]);

  const isRubricComplete = (row) =>
    Boolean(row?.id?.trim() && row?.rule?.trim());

  const hasIncompleteLeadingCreated =
    rubrics.length > 0 &&
    rubrics[0].created &&
    !rubrics[0].deleted &&
    !isRubricComplete(rubrics[0]);

  const duplicateIds = useMemo(() => {
    const seen = new Set();
    const dups = new Set();
    activeRubrics.forEach((row) => {
      const id = row.id?.trim();
      if (!id) return;
      if (seen.has(id)) dups.add(id);
      else seen.add(id);
    });
    return dups;
  }, [activeRubrics]);

  const hasDuplicateIds = duplicateIds.size > 0;

  const pendingDiff = useMemo(() => {
    const created = [];
    const changed = [];
    const deleted = [];

    rubrics.forEach((row) => {
      if (row.created) {
        if (!row.deleted && isRubricComplete(row)) {
          created.push(toRubricPayload(row));
        }
        return;
      }
      if (row.deleted) {
        const id = (row._originalId || row.id || '').trim();
        if (id) deleted.push({ id });
        return;
      }
      if (row.changed && isRubricComplete(row)) {
        const originalId = (row._originalId || '').trim();
        const nextId = row.id.trim();
        if (originalId && nextId !== originalId) {
          deleted.push({ id: originalId });
          created.push(toRubricPayload(row));
        } else {
          changed.push(toRubricPayload(row));
        }
      }
    });

    return { created, changed, deleted };
  }, [rubrics]);

  const hasPendingChanges =
    pendingDiff.created.length +
      pendingDiff.changed.length +
      pendingDiff.deleted.length >
    0;

  const createdRowsComplete = rubrics
    .filter((r) => r.created && !r.deleted)
    .every(isRubricComplete);

  const changedRowsComplete = rubrics
    .filter((r) => r.changed && !r.deleted && !r.created)
    .every(isRubricComplete);

  const canSave =
    editRubrics &&
    hasPendingChanges &&
    createdRowsComplete &&
    changedRowsComplete &&
    !hasDuplicateIds;

  const handleRubricField = (index, field, value) => {
    setRubrics((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        const next = { ...row, [field]: value };
        if (next.created || next.deleted) {
          return next;
        }
        const idChanged =
          (next.id || '').trim() !== (next._originalId || '').trim();
        const ruleChanged = (next.rule || '') !== (next._originalRule || '');
        const severityChanged =
          (next.severity || '') !== (next._originalSeverity || '');
        const messageChanged =
          (next.message || '') !== (next._originalMessage || '');
        const shownChanged =
          !!next.shownInPhase01 !== !!next._originalShownInPhase01;
        const occursChanged =
          (next.occursInPractice || '') !==
          (next._originalOccursInPractice || '');
        const noteChanged = (next.note || '') !== (next._originalNote || '');
        const blockChanged = (next.block || '') !== (next._originalBlock || '');
        const detectedChanged =
          (next.detectedBy || '') !== (next._originalDetectedBy || '');
        next.changed =
          idChanged ||
          ruleChanged ||
          severityChanged ||
          messageChanged ||
          shownChanged ||
          occursChanged ||
          noteChanged ||
          blockChanged ||
          detectedChanged;
        return next;
      }),
    );
  };

  const handleAdd = () => {
    if (!task || busy || hasIncompleteLeadingCreated) return;
    setEditRubrics(true);
    setRubrics((prev) => [
      {
        created: true,
        id: '',
        rule: '',
        severity: 'minor',
        message: '',
        shownInPhase01: false,
        occursInPractice: null,
        note: null,
        example: null,
        block: null,
        detectedBy: null,
      },
      ...prev,
    ]);
  };

  const handleEditChange = (checked) => {
    setEditRubrics(checked);
    if (!checked) {
      setRubrics(rubricsBaseline.map((row) => ({ ...row })));
    }
  };

  const handleDelete = (index) => {
    if (!editRubrics || busy) return;
    setRubrics((prev) => {
      const row = prev[index];
      if (!row) return prev;
      if (row.created) {
        return prev.filter((_, i) => i !== index);
      }
      return prev.map((r, i) =>
        i === index ? { ...r, deleted: !r.deleted } : r,
      );
    });
  };

  const handleSave = () => {
    if (!task?.task_type || !canSave || saveLoading) return;
    setSaveLoading(true);
    OutwriteApi.saveRubrics(task.task_type, pendingDiff)
      .then((saved) => {
        if (Array.isArray(saved)) {
          const mapped = mapLoadedRubrics(saved);
          setRubrics(mapped);
          setRubricsBaseline(mapped);
          setEditRubrics(false);
        }
      })
      .catch(() => {})
      .finally(() => setSaveLoading(false));
  };

  const handleDownloadRubrics = () => {
    if (downloadLoading) return;
    setDownloadLoading(true);
    OutwriteApi.exportRubricsCatalog()
      .then((catalog) => {
        const blob = new Blob([JSON.stringify(catalog, null, 2)], {
          type: 'application/json',
        });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = 'rubrics.json';
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(url);
      })
      .catch(() => {})
      .finally(() => setDownloadLoading(false));
  };

  const handleCheck = () => {
    const text = answerText.trim();
    if (!text || !selectedId || checkLoading) {
      return;
    }
    setCheckLoading(true);
    setCheckResult(null);
    setCheckAnnotations({});
    setMakeTestMessage(null);
    const payloadRubrics = editRubrics
      ? activeRubrics.map((r) => ({ id: r.id, rule: r.rule }))
      : undefined;
    const payloadPrompt = editPrompt ? promptRules : undefined;
    OutwriteApi.check(
      text,
      selectedId,
      CHECK_TIMEOUT_MS,
      payloadRubrics,
      payloadPrompt,
    )
      .then((data) => {
        setCheckResult(data);
        const next = {};
        (Array.isArray(data?.assessment_results)
          ? data.assessment_results
          : []
        ).forEach((row) => {
          if (!row?.rubric_id) return;
          next[row.rubric_id] = { expected: true, comment: '' };
        });
        setCheckAnnotations(next);
      })
      .catch(() => {})
      .finally(() => {
        setCheckLoading(false);
      });
  };

  const handleAnnotationExpected = (rubricId, expected) => {
    setCheckAnnotations((prev) => ({
      ...prev,
      [rubricId]: {
        expected: !!expected,
        comment: prev[rubricId]?.comment ?? '',
      },
    }));
  };

  const handleAnnotationComment = (rubricId, comment) => {
    setCheckAnnotations((prev) => ({
      ...prev,
      [rubricId]: {
        expected: prev[rubricId]?.expected !== false,
        comment,
      },
    }));
  };

  const currentAnnotationPayload = () =>
    (checkResult?.assessment_results || []).map((row) => {
      const ann = checkAnnotations[row.rubric_id] || {};
      return {
        rubric_id: row.rubric_id,
        expected: ann.expected !== false,
        comment: ann.comment ?? '',
      };
    });

  const handleSaveAnnotations = () => {
    const checkLogId = checkResult?.checkLogId;
    if (!checkLogId || annotationSaveLoading) return;
    const annotations = currentAnnotationPayload();
    setAnnotationSaveLoading(true);
    OutwriteApi.saveCheckAnnotations(checkLogId, annotations)
      .then((saved) => {
        const next = {};
        (Array.isArray(saved?.annotations) ? saved.annotations : annotations).forEach(
          (row) => {
            if (!row?.rubric_id) return;
            next[row.rubric_id] = {
              expected: row.expected !== false,
              comment: row.comment ?? '',
            };
          },
        );
        setCheckAnnotations(next);
      })
      .catch(() => {})
      .finally(() => setAnnotationSaveLoading(false));
  };

  const handleMakeTest = () => {
    const checkLogId = checkResult?.checkLogId;
    if (!checkLogId || makeTestLoading) return;
    setMakeTestLoading(true);
    setMakeTestMessage(null);
    OutwriteApi.createTestCase(checkLogId, {
      annotations: currentAnnotationPayload(),
    })
      .then((created) => {
        setMakeTestMessage(
          created?.id
            ? `Test case #${created.id} created`
            : 'Test case created',
        );
      })
      .catch(() => {
        setMakeTestMessage('Failed to create test case');
      })
      .finally(() => setMakeTestLoading(false));
  };

  const handleToFindField = (index, field, value) => {
    setToFinds((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
    );
  };

  const handleToFindOptions = (index, value) => {
    const options = String(value)
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
    handleToFindField(index, 'options', options);
  };

  const handleSaveToFinds = () => {
    if (!selectedId || toFindsSaveLoading) return;
    setToFindsSaveLoading(true);
    OutwriteApi.saveToFinds(selectedId, toFinds)
      .then((data) => {
        const finds = Array.isArray(data?.to_finds)
          ? data.to_finds.map((row) => ({ ...row }))
          : toFinds.map((row) => ({ ...row }));
        setToFinds(finds);
        setToFindsBaseline(finds.map((row) => ({ ...row })));
        setTask((prev) => (prev ? { ...prev, to_finds: finds } : prev));
        setEditToFinds(false);
      })
      .catch(() => {})
      .finally(() => setToFindsSaveLoading(false));
  };

  const handleSavePrompt = () => {
    if (!task?.task_type || promptSaveLoading) return;
    setPromptSaveLoading(true);
    OutwriteApi.saveCheckPrompt(task.task_type, promptRules)
      .then((saved) => {
        const text = saved?.rulesText || promptRules;
        setPromptRules(text);
        setPromptBaseline(text);
        setEditPrompt(false);
      })
      .catch(() => {})
      .finally(() => setPromptSaveLoading(false));
  };

  const busy =
    checkLoading ||
    annotationSaveLoading ||
    makeTestLoading ||
    saveLoading ||
    downloadLoading ||
    toFindsSaveLoading ||
    promptSaveLoading;

  const taskKeys = Array.isArray(task?.keys) ? task.keys : [];

  return (
    <Box sx={{ p: 3, maxWidth: 1280, mx: 'auto' }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Typography variant="h5" component="h1">
          Outwrite tasks
        </Typography>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          <FormControl sx={{ minWidth: 280 }} size="small" disabled={listLoading}>
            <InputLabel id="outwrite-task-select-label">Task</InputLabel>
            <Select
              labelId="outwrite-task-select-label"
              id="outwrite-task-select"
              label="Task"
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
            >
              {tasks.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.id} (difficulty {t.difficulty})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <OutwriteLlmModelSelect disabled={busy} />
        </Stack>
      </Stack>

      {listError && (
        <Typography color="error" sx={{ mb: 2 }}>
          {listError}
        </Typography>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '3fr 2fr' },
          gap: 2,
          alignItems: 'stretch',
          position: 'relative',
          opacity: taskLoading ? 0.72 : 1,
          transition: 'opacity 120ms ease',
        }}
      >
        {(listLoading || taskLoading) && (
          <CircularProgress
            size={36}
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              ml: '-18px',
              mt: '-18px',
              zIndex: 1,
            }}
          />
        )}

        <Box
          sx={{
            width: '100%',
            minHeight: 420,
            bgcolor: '#fcfcfb',
            border: '1px solid #e4e4e1',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {!listLoading && option && (
            <ReactECharts
              option={option}
              style={{ height: 420, width: '100%' }}
              notMerge
              lazyUpdate
            />
          )}
          {!listLoading && !option && !listError && (
            <Typography color="text.secondary">
              Select a task to render
            </Typography>
          )}
        </Box>

        {!listLoading && task && <OutwriteTaskReviewPanel task={task} />}
      </Box>

      <Stack spacing={2} sx={{ mt: 3 }}>
        <OutwriteCollapsibleTable
          title="Keys"
          summary={
            taskKeys.length === 1 ? '1 key' : `${taskKeys.length} keys`
          }
          columns={[
            { key: 'id', label: 'id', width: '22%' },
            { key: 'value', label: 'value' },
          ]}
        >
          {taskKeys.map((key) => (
            <TableRow key={key.id}>
              <TableCell>{key.id}</TableCell>
              <TableCell sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 12 }}>
                {formatKeyValue(key.value)}
              </TableCell>
            </TableRow>
          ))}
          {!taskLoading && taskKeys.length === 0 && (
            <TableRow>
              <TableCell colSpan={2}>
                <Typography variant="body2" color="text.secondary">
                  No keys on this task.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </OutwriteCollapsibleTable>

        <OutwriteCollapsibleTable
          title="ToFinds"
          summary={
            toFinds.length === 1 ? '1 item' : `${toFinds.length} items`
          }
          columns={[
            { key: 'id', label: 'id', width: '14%' },
            { key: 'key_id', label: 'key_id', width: '12%' },
            { key: 'prompt', label: 'prompt', width: '28%' },
            { key: 'options', label: 'options' },
            { key: 'explanation', label: 'explanation', width: '18%' },
          ]}
          actions={
            <Stack direction="row" spacing={1} alignItems="center">
              <FormControlLabel
                sx={{ mr: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={editToFinds}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setEditToFinds(checked);
                      if (!checked) {
                        setToFinds(toFindsBaseline.map((row) => ({ ...row })));
                      }
                    }}
                    disabled={!task || busy}
                  />
                }
                label="Edit"
              />
              <Button
                size="small"
                variant="outlined"
                onClick={handleSaveToFinds}
                disabled={!task || busy || !editToFinds}
              >
                {toFindsSaveLoading ? 'Saving…' : 'Save'}
              </Button>
            </Stack>
          }
        >
          {toFinds.map((row, index) => (
            <TableRow key={`${row.id || 'tf'}-${index}`}>
              <TableCell>
                {editToFinds ? (
                  <TextField
                    size="small"
                    fullWidth
                    value={row.id || ''}
                    onChange={(e) =>
                      handleToFindField(index, 'id', e.target.value)
                    }
                    disabled={busy}
                  />
                ) : (
                  row.id
                )}
              </TableCell>
              <TableCell>
                {editToFinds ? (
                  <TextField
                    size="small"
                    fullWidth
                    value={row.key_id || ''}
                    onChange={(e) =>
                      handleToFindField(index, 'key_id', e.target.value)
                    }
                    disabled={busy}
                  />
                ) : (
                  row.key_id || ''
                )}
              </TableCell>
              <TableCell>
                {editToFinds ? (
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    value={row.prompt || ''}
                    onChange={(e) =>
                      handleToFindField(index, 'prompt', e.target.value)
                    }
                    disabled={busy}
                  />
                ) : (
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {row.prompt || ''}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                {editToFinds ? (
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    value={(row.options || []).join('\n')}
                    onChange={(e) => handleToFindOptions(index, e.target.value)}
                    disabled={busy}
                    helperText="One option per line"
                  />
                ) : (
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {(row.options || []).join('\n')}
                  </Typography>
                )}
              </TableCell>
              <TableCell sx={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>
                {editToFinds ? (
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    value={formatExplanation(row.explanation)}
                    onChange={(e) => {
                      const raw = e.target.value;
                      let next = raw;
                      try {
                        next = JSON.parse(raw);
                      } catch {
                        next = raw;
                      }
                      handleToFindField(index, 'explanation', next);
                    }}
                    disabled={busy}
                  />
                ) : (
                  formatExplanation(row.explanation)
                )}
              </TableCell>
            </TableRow>
          ))}
          {!taskLoading && toFinds.length === 0 && (
            <TableRow>
              <TableCell colSpan={5}>
                <Typography variant="body2" color="text.secondary">
                  No to_finds on this task.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </OutwriteCollapsibleTable>

        <Box
          sx={{
            border: '1px solid #d0d0cc',
            borderRadius: 1,
            bgcolor: '#fcfcfb',
            p: 1.5,
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ sm: 'center' }}
            justifyContent="space-between"
            sx={{ mb: 1 }}
          >
            <Typography sx={{ fontWeight: 700 }}>
              Check prompt rules
              {task?.task_type ? ` (${task.task_type})` : ''}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <FormControlLabel
                sx={{ mr: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={editPrompt}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setEditPrompt(checked);
                      if (!checked) setPromptRules(promptBaseline);
                    }}
                    disabled={!task || busy}
                  />
                }
                label="Edit & Override"
              />
              <Button
                size="small"
                variant="outlined"
                onClick={handleSavePrompt}
                disabled={!task || busy || !editPrompt}
              >
                {promptSaveLoading ? 'Saving…' : 'Save'}
              </Button>
            </Stack>
          </Stack>
          <TextField
            fullWidth
            multiline
            minRows={6}
            value={promptRules}
            onChange={(e) => setPromptRules(e.target.value)}
            disabled={!editPrompt || busy || !task}
            placeholder="Rules block injected into the LLM check prompt"
          />
        </Box>

        <OutwriteCollapsibleTable
          title={
            task?.task_type ? `Rubrics (${task.task_type})` : 'Rubrics'
          }
          summary={
            rubrics.length === 1
              ? '1 item'
              : `${rubrics.length} items`
          }
          columns={[
            { key: 'rubric_id', label: 'rubric_id', width: '10%' },
            { key: 'severity', label: 'severity', width: '9%' },
            { key: 'phase01', label: 'phase01', width: '7%' },
            { key: 'occurs', label: 'occurs', width: '9%' },
            { key: 'message', label: 'message', width: '14%' },
            { key: 'rule', label: 'rule', width: '18%' },
            { key: 'note', label: 'note', width: '10%' },
            { key: 'block', label: 'block', width: '7%' },
            { key: 'detectedBy', label: 'detectedBy', width: '10%' },
            ...(editRubrics
              ? [{ key: 'actions', label: '', width: 56 }]
              : []),
          ]}
          actions={
            <Stack direction="row" spacing={1} alignItems="center">
              <FormControlLabel
                sx={{ mr: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={editRubrics}
                    onChange={(e) => handleEditChange(e.target.checked)}
                    disabled={!task || busy}
                  />
                }
                label="Edit & Override"
              />
              <Button
                size="small"
                variant="outlined"
                onClick={handleAdd}
                disabled={!task || busy || hasIncompleteLeadingCreated}
              >
                Add
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={handleSave}
                disabled={!task || busy || !canSave}
              >
                {saveLoading ? 'Saving…' : 'Save'}
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={handleDownloadRubrics}
                disabled={busy}
              >
                {downloadLoading ? 'Downloading…' : 'Download'}
              </Button>
            </Stack>
          }
        >
          {rubrics.map((row, index) => {
            const trimmedId = row.id?.trim() || '';
            const idConflict =
              !row.deleted &&
              Boolean(trimmedId) &&
              duplicateIds.has(trimmedId);
            const idChanged =
              row.changed &&
              !row.created &&
              !row.deleted &&
              trimmedId !== (row._originalId || '').trim();
            const severityChanged =
              row.changed &&
              !row.created &&
              !row.deleted &&
              (row.severity || '') !== (row._originalSeverity || '');
            const messageChanged =
              row.changed &&
              !row.created &&
              !row.deleted &&
              (row.message || '') !== (row._originalMessage || '');
            const ruleChanged =
              row.changed &&
              !row.created &&
              !row.deleted &&
              (row.rule || '') !== (row._originalRule || '');
            const shownChanged =
              row.changed &&
              !row.created &&
              !row.deleted &&
              !!row.shownInPhase01 !== !!row._originalShownInPhase01;
            const occursChanged =
              row.changed &&
              !row.created &&
              !row.deleted &&
              (row.occursInPractice || '') !==
                (row._originalOccursInPractice || '');
            const noteChanged =
              row.changed &&
              !row.created &&
              !row.deleted &&
              (row.note || '') !== (row._originalNote || '');
            const blockChanged =
              row.changed &&
              !row.created &&
              !row.deleted &&
              (row.block || '') !== (row._originalBlock || '');
            const detectedChanged =
              row.changed &&
              !row.created &&
              !row.deleted &&
              (row.detectedBy || '') !== (row._originalDetectedBy || '');
            const rowBg = row.deleted
              ? ROW_DELETED_BG
              : row.created
                ? ROW_CREATED_BG
                : undefined;
            const cellBg = (changed) =>
              changed
                ? { bgcolor: CELL_CHANGED_BG }
                : rowBg
                  ? { bgcolor: rowBg }
                  : undefined;
            const rowKey = row.created
              ? `created-${index}`
              : `${row._originalId || row.id}-${index}`;
            return (
              <TableRow
                key={rowKey}
                sx={rowBg ? { bgcolor: rowBg } : undefined}
              >
                <TableCell sx={cellBg(idChanged)}>
                  {editRubrics ? (
                    <TextField
                      size="small"
                      fullWidth
                      value={row.id}
                      onChange={(e) =>
                        handleRubricField(index, 'id', e.target.value)
                      }
                      disabled={busy || row.deleted}
                      error={idConflict}
                      helperText={
                        idConflict ? 'ID already exists' : undefined
                      }
                    />
                  ) : (
                    row.id
                  )}
                </TableCell>
                <TableCell sx={cellBg(severityChanged)}>
                  {editRubrics ? (
                    <FormControl
                      size="small"
                      fullWidth
                      disabled={busy || row.deleted}
                    >
                      <Select
                        value={row.severity ?? ''}
                        displayEmpty
                        onChange={(e) =>
                          handleRubricField(index, 'severity', e.target.value)
                        }
                      >
                        {row.severity &&
                          !severityOptions.includes(row.severity) && (
                            <MenuItem value={row.severity} disabled>
                              {row.severity}
                            </MenuItem>
                          )}
                        {severityOptions.map((opt) => (
                          <MenuItem key={opt} value={opt}>
                            {opt}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    row.severity || ''
                  )}
                </TableCell>
                <TableCell sx={cellBg(shownChanged)}>
                  {editRubrics ? (
                    <Checkbox
                      size="small"
                      checked={!!row.shownInPhase01}
                      onChange={(e) =>
                        handleRubricField(
                          index,
                          'shownInPhase01',
                          e.target.checked,
                        )
                      }
                      disabled={busy || row.deleted}
                    />
                  ) : row.shownInPhase01 ? (
                    'yes'
                  ) : (
                    ''
                  )}
                </TableCell>
                <TableCell sx={cellBg(occursChanged)}>
                  {editRubrics ? (
                    <TextField
                      size="small"
                      fullWidth
                      value={row.occursInPractice ?? ''}
                      onChange={(e) =>
                        handleRubricField(
                          index,
                          'occursInPractice',
                          e.target.value,
                        )
                      }
                      disabled={busy || row.deleted}
                    />
                  ) : (
                    row.occursInPractice || ''
                  )}
                </TableCell>
                <TableCell sx={cellBg(messageChanged)}>
                  {editRubrics ? (
                    <TextField
                      size="small"
                      fullWidth
                      multiline
                      minRows={2}
                      value={row.message ?? ''}
                      onChange={(e) =>
                        handleRubricField(index, 'message', e.target.value)
                      }
                      disabled={busy || row.deleted}
                    />
                  ) : (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {row.message || ''}
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={cellBg(ruleChanged)}>
                  {editRubrics ? (
                    <TextField
                      size="small"
                      fullWidth
                      multiline
                      minRows={2}
                      value={row.rule}
                      onChange={(e) =>
                        handleRubricField(index, 'rule', e.target.value)
                      }
                      disabled={busy || row.deleted}
                    />
                  ) : (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {row.rule}
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={cellBg(noteChanged)}>
                  {editRubrics ? (
                    <TextField
                      size="small"
                      fullWidth
                      multiline
                      minRows={1}
                      value={row.note ?? ''}
                      onChange={(e) =>
                        handleRubricField(index, 'note', e.target.value)
                      }
                      disabled={busy || row.deleted}
                    />
                  ) : (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {row.note || ''}
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={cellBg(blockChanged)}>
                  {editRubrics ? (
                    <TextField
                      size="small"
                      fullWidth
                      value={row.block ?? ''}
                      onChange={(e) =>
                        handleRubricField(index, 'block', e.target.value)
                      }
                      disabled={busy || row.deleted}
                    />
                  ) : (
                    row.block || ''
                  )}
                </TableCell>
                <TableCell sx={cellBg(detectedChanged)}>
                  {editRubrics ? (
                    <TextField
                      size="small"
                      fullWidth
                      multiline
                      minRows={1}
                      value={row.detectedBy ?? ''}
                      onChange={(e) =>
                        handleRubricField(index, 'detectedBy', e.target.value)
                      }
                      disabled={busy || row.deleted}
                    />
                  ) : (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {row.detectedBy || ''}
                    </Typography>
                  )}
                </TableCell>
                {editRubrics && (
                  <TableCell
                    align="right"
                    sx={{ width: 56, ...(rowBg ? { bgcolor: rowBg } : {}) }}
                  >
                    <IconButton
                      size="small"
                      aria-label={
                        row.deleted
                          ? `Restore rubric ${trimmedId || index}`
                          : trimmedId
                            ? `Delete rubric ${trimmedId}`
                            : 'Delete created rubric'
                      }
                      disabled={busy}
                      onClick={() => handleDelete(index)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
          {!taskLoading && rubrics.length === 0 && (
            <TableRow>
              <TableCell colSpan={editRubrics ? 10 : 9}>
                <Typography variant="body2" color="text.secondary">
                  No rubrics for this task type.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </OutwriteCollapsibleTable>

        <TextField
          label="Your answer"
          value={answerText}
          onChange={(event) => setAnswerText(event.target.value)}
          fullWidth
          multiline
          minRows={8}
          disabled={busy || !selectedId}
        />
        <Box>
          <Button
            variant="contained"
            onClick={handleCheck}
            disabled={busy || !answerText.trim() || !selectedId}
            startIcon={
              checkLoading ? (
                <CircularProgress size={18} color="inherit" />
              ) : null
            }
          >
            {checkLoading ? 'Checking…' : 'CHECK'}
          </Button>
        </Box>

        <OutwriteCollapsibleTable
          title="Check result"
          summary={
            checkResult?.assessment_results
              ? `${checkResult.assessment_results.length} result${
                  checkResult.assessment_results.length === 1 ? '' : 's'
                }`
              : 'empty'
          }
          columns={[
            { key: 'rubric_id', label: 'rubric_id' },
            { key: 'rule', label: 'rule' },
            { key: 'result', label: 'result', width: 90 },
            { key: 'failed_at', label: 'failed_at' },
            { key: 'note', label: 'note' },
            { key: 'expected', label: 'Expected', width: 100 },
            { key: 'comment', label: 'Comment' },
          ]}
        >
          {(checkResult?.assessment_results || []).map((row, index) => {
            const result = String(row.result || '').toUpperCase();
            const isPass = result === 'PASS';
            const isFail = result === 'FAIL';
            const ann = checkAnnotations[row.rubric_id] || {
              expected: true,
              comment: '',
            };
            return (
              <TableRow key={`${row.rubric_id}-${index}`}>
                <TableCell>{row.rubric_id}</TableCell>
                <TableCell sx={{ whiteSpace: 'pre-wrap' }}>
                  {ruleById.get(row.rubric_id) || ''}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    color: isPass
                      ? 'success.main'
                      : isFail
                        ? 'error.main'
                        : 'text.primary',
                  }}
                >
                  {row.result}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'pre-wrap' }}>
                  {Array.isArray(row.failed_at)
                    ? row.failed_at.join('\n')
                    : ''}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'pre-wrap' }}>
                  {row.note || ''}
                </TableCell>
                <TableCell padding="checkbox" align="center">
                  <Checkbox
                    checked={ann.expected !== false}
                    onChange={(event) =>
                      handleAnnotationExpected(
                        row.rubric_id,
                        event.target.checked,
                      )
                    }
                    inputProps={{
                      'aria-label': `Expected for ${row.rubric_id}`,
                    }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    minRows={1}
                    maxRows={4}
                    value={ann.comment ?? ''}
                    onChange={(event) =>
                      handleAnnotationComment(
                        row.rubric_id,
                        event.target.value,
                      )
                    }
                    placeholder="optional"
                  />
                </TableCell>
              </TableRow>
            );
          })}
          {!checkResult && (
            <TableRow>
              <TableCell colSpan={7}>
                <Typography variant="body2" color="text.secondary">
                  Run CHECK to see assessment results.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </OutwriteCollapsibleTable>
        {checkResult?.assessment_results?.length > 0 && (
          <Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined"
                onClick={handleSaveAnnotations}
                disabled={
                  busy ||
                  annotationSaveLoading ||
                  !checkResult?.checkLogId
                }
                startIcon={
                  annotationSaveLoading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : null
                }
              >
                {annotationSaveLoading
                  ? 'Saving…'
                  : 'Save Expected / Comment'}
              </Button>
              <Button
                variant="contained"
                onClick={handleMakeTest}
                disabled={
                  busy || makeTestLoading || !checkResult?.checkLogId
                }
                startIcon={
                  makeTestLoading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : null
                }
              >
                {makeTestLoading ? 'Creating…' : 'Make Test'}
              </Button>
            </Stack>
            {makeTestMessage && (
              <Typography
                variant="body2"
                color={
                  makeTestMessage.startsWith('Failed')
                    ? 'error.main'
                    : 'success.main'
                }
                sx={{ mt: 0.5 }}
              >
                {makeTestMessage}
              </Typography>
            )}
            {!checkResult?.checkLogId && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.5 }}
              >
                Check log id missing — annotations / Make Test unavailable.
              </Typography>
            )}
          </Box>
        )}
      </Stack>
    </Box>
  );
}
